import React from "react";
import { Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTodayReservations } from "./TodayReservationsProvider";
import { reservationItemCount, reservationSendText, reservationTiming } from "./reservationUtils";

const RESERVATION_MENU_ID = 14;
const MAX_ROWS = 3;

const timingStyle = {
  overdue: { background: "#FDECEC", color: "#B30000", border: "rgba(179,0,0,0.28)" },
  due: { background: "#FFF4E5", color: "#B45309", border: "rgba(245,124,0,0.35)" },
  upcoming: { background: "#FFFBEB", color: "#536073", border: "rgba(32,33,36,0.08)" },
};

// 首頁「今日預約」卡片：待處理筆數＋最近三筆，時間快到的用琥珀／紅色標示。
export default function TodayReservationsCard({ onSelectTab }) {
  const navigate = useNavigate();
  const { reservations, nowMs } = useTodayReservations();

  const goReservations = () => {
    navigate("/reservations");
    if (onSelectTab) onSelectTab(RESERVATION_MENU_ID);
  };

  const upcoming = [...reservations]
    .sort((a, b) => dayjs(a.get_time).valueOf() - dayjs(b.get_time).valueOf())
    .slice(0, MAX_ROWS);

  return (
    <Box
      sx={{
        mt: 3,
        backgroundColor: "#FFF",
        borderRadius: "22px",
        border: "1px solid rgba(32,33,36,0.08)",
        boxShadow: "0 10px 30px rgba(31, 35, 40, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid rgba(32,33,36,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ color: "#202124", fontSize: 20, fontWeight: 800 }}>
            今日預約{reservations.length ? `（${reservations.length}）` : ""}
          </Typography>
          <Typography sx={{ color: "#536073", fontSize: 13 }}>
            標籤為預計送到出餐管理的時間，快到會提醒
          </Typography>
        </Box>
        <Button
          onClick={goReservations}
          sx={{ color: "#1C2D42", fontWeight: 800, borderRadius: "999px", px: 2 }}
        >
          前往管理
        </Button>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {upcoming.length ? (
          upcoming.map((order) => {
            const timing = reservationTiming(order, nowMs);
            // 沒預點（到店才點）沒東西要送出餐，維持一般樣式
            const style = timing !== "overdue" && !reservationItemCount(order)
              ? timingStyle.upcoming
              : timingStyle[timing] || timingStyle.upcoming;
            return (
              <Box
                key={order.order_id}
                onClick={goReservations}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: "14px",
                  cursor: "pointer",
                  backgroundColor: style.background,
                  border: "1px solid",
                  borderColor: style.border,
                  "&:hover": { filter: "brightness(0.97)" },
                }}
              >
                <Typography sx={{ color: "#202124", fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
                  {dayjs(order.get_time).format("HH:mm")}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: "#202124",
                    fontSize: 16,
                    fontWeight: 800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {order.name || "-"}
                </Typography>
                <Typography sx={{ color: "#536073", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                  {Number(order.guest_count || 0)} 人
                </Typography>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 800,
                    px: 1,
                    py: 0.25,
                    borderRadius: "999px",
                    color: style.color,
                    backgroundColor: "#FFF",
                    border: "1px solid",
                    borderColor: style.border,
                  }}
                >
                  {reservationSendText(order, nowMs)}
                </Box>
              </Box>
            );
          })
        ) : (
          <Box
            sx={{
              width: "100%",
              py: 2,
              textAlign: "center",
              color: "#536073",
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            今日尚無待處理預約
          </Box>
        )}
      </Box>
    </Box>
  );
}
